import React from 'react';

import {
  useDismissedBannerQuery,
  useSetDismissedBannerMutation,
} from '../../../../generated/graphql';

type BannerComponent<T> = React.FC<T & {onDismiss: () => Promise<void>}>;

type DismissibleBanner<T> = {
  Component: BannerComponent<T>;
  id: (props: T) => string;
  showAfter?: number;
};

const useDismissedBannerCheck = (bannerId: string) => {
  const [setDismissedBanner] = useSetDismissedBannerMutation({
    variables: {
      id: bannerId,
    },
  });

  const {data, loading, refetch} = useDismissedBannerQuery({
    variables: {
      id: bannerId,
    },
  });

  const onDismiss = async () => {
    await setDismissedBanner();
    await refetch();
  };

  if (loading) {
    return {
      dismissed: false,
      loading,
      onDismiss,
    };
  }

  if (data?.dismissedBanner?.dismissedAt) {
    const today = new Date().getTime();
    const bannerDismissedAt = new Date(
      data.dismissedBanner.dismissedAt
    ).getTime();
    const timeAgoBannerWasDismissed = today - bannerDismissedAt;
    if (timeAgoBannerWasDismissed < 1000 * 60 * 60 * 24 * 7) {
      return {
        dismissed: true,
        loading,
        onDismiss,
      };
    }
  }

  return {
    dismissed: false,
    loading,
    onDismiss,
  };
};

export const createDismissibleBanner = <T extends object>(
  b: DismissibleBanner<T>
): React.FC<T> => {
  return (props: T) => {
    const bannerId = typeof b.id === 'function' ? b.id(props) : b.id;

    const {dismissed, loading, onDismiss} = useDismissedBannerCheck(bannerId);

    if (loading || dismissed) {
      return null;
    }

    return (
      <b.Component
        {...props}
        onDismiss={async () => {
          await onDismiss();
        }}
      />
    );
  };
};
